"use client";

import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Button,
  Container,
  Notification,
  PasswordInput,
  TextInput,
  Title,
} from "@mantine/core";
import styles from "./login.module.scss";

type AuthRole = "superAdmin" | "admin" | "collaborator" | "user";

const getRedirectPath = (role?: AuthRole) => {
  if (role === "superAdmin" || role === "admin") return "/jarvis";
  if (role === "collaborator") return "/admin/team";
  return "/login";
};

const LoginPage = () => {
  const router = useRouter();
  const startedRef = useRef(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const checkSession = async () => {
      try {
        const res = await fetch("/api/validate-token", {
          method: "GET",
          credentials: "include",
          cache: "no-store",
        });

        if (!res.ok) {
          setCheckingSession(false);
          return;
        }

        const data = await res.json();
        const role = data?.user?.role as AuthRole | undefined;

        if (role) {
          router.replace(getRedirectPath(role));
          return;
        }

        setCheckingSession(false);
      } catch {
        setCheckingSession(false);
      }
    };

    checkSession();
  }, [router]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const response = await fetch("/api/authenticate", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || "Identifiants invalides");
      }

      const role = data?.user?.role as AuthRole | undefined;
      router.replace(getRedirectPath(role));
      router.refresh();
    } catch (error: any) {
      setError(error?.message || "Erreur inattendue");
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingSession) {
    return (
      <div className={styles.pageWrapper}>
        <Container size="xs" className={styles.loginContainer}>
          <Title order={2} className={styles.title}>
            Vérification…
          </Title>
        </Container>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <Container size="xs" className={styles.loginContainer}>
        <Title order={2} className={styles.title}>
          Connexion
        </Title>

        {error ? (
          <Notification
            color="red"
            title="Erreur"
            onClose={() => setError("")}
            mt="md"
          >
            {error}
          </Notification>
        ) : null}

        <form onSubmit={handleLogin}>
          <TextInput
            name="username"
            placeholder="Nom d'utilisateur"
            value={username}
            onChange={(e) => setUsername(e.currentTarget.value)}
            className={styles.input}
            autoComplete="username"
          />

          <PasswordInput
            name="password"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            className={styles.input}
            mt="md"
            autoComplete="current-password"
          />

          <Button
            type="submit"
            className={styles.button}
            mt="md"
            fullWidth
            loading={submitting}
          >
            Connexion
          </Button>
        </form>
      </Container>
    </div>
  );
};

export default LoginPage;